import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Check } from 'lucide-react';

interface Option {
  id: string;
  label: string;
}

interface OnboardingOptionChipsProps {
  options: Option[];
  multiSelect?: boolean;
  disabled?: boolean;
  onSelect: (selected: string[]) => void;
}

export function OnboardingOptionChips({ options, multiSelect = false, disabled = false, onSelect }: OnboardingOptionChipsProps) {
  const [selected, setSelected] = useState<string[]>([]);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const toggleOption = (id: string) => {
    if (isSubmitted || disabled) return;
    
    if (multiSelect) {
      setSelected(prev => 
        prev.includes(id) ? prev.filter(s => s !== id) : [...prev, id]
      );
    } else {
      // Single select - submit immediately
      setIsSubmitted(true);
      // Pass the id, not the label, so handlers can use the raw value
      onSelect([id]);
    }
  };

  const handleConfirm = () => {
    if (selected.length === 0) return;
    setIsSubmitted(true);
    // Pass the ids, not the labels, so handlers can use the raw values
    onSelect(selected);
  };

  if (isSubmitted) return null;

  return (
    <div className="ml-6 sm:ml-12 mt-3 space-y-3">
      <div className="flex flex-wrap gap-2">
        {options.map((option) => {
          const isSelected = selected.includes(option.id);
          return (
            <button
              key={option.id}
              onClick={() => toggleOption(option.id)}
              className={cn(
                'px-3 py-2 sm:px-4 sm:py-2.5 rounded-md font-mono text-[10px] uppercase tracking-[0.1em] transition-all duration-200',
                'border bg-transparent',
                disabled
                  ? 'border-white/[0.08] text-white/30 cursor-not-allowed'
                  : isSelected
                    ? 'border-[rgba(0,255,200,0.6)] text-[#00FFC8] shadow-[0_0_20px_rgba(0,255,200,0.35),0_0_40px_rgba(0,255,200,0.15)] bg-[rgba(0,255,200,0.1)]'
                    : 'border-white/[0.15] text-foreground hover:border-[#00FFC8]/40 hover:text-[#00FFC8] hover:shadow-[0_0_12px_rgba(0,255,200,0.15)]'
              )}
            >
              {multiSelect && isSelected && (
                <Check className="h-3.5 w-3.5 inline mr-1.5" />
              )}
              {option.label}
            </button>
          );
        })}
      </div>

      {multiSelect && selected.length > 0 && (
        <Button
          onClick={handleConfirm}
          size="sm"
          className={cn(
            'font-mono text-[10px] uppercase tracking-[0.12em] border rounded-md transition-all duration-150',
            'border-[rgba(0,255,200,0.4)] bg-[rgba(0,255,200,0.1)] text-[#00FFC8]',
            'hover:bg-[rgba(0,255,200,0.18)] hover:shadow-[0_0_24px_rgba(0,255,200,0.25)]',
            'active:scale-95 active:shadow-[0_0_8px_rgba(0,255,200,0.4)]'
          )}
        >
          Commit Changes
        </Button>
      )}
    </div>
  );
}
